<template>
  <a-modal
      :width="isMobile ? '100%' : 650"
      :footer="null"
      :open="open"
      @cancel="closeForm"
      class="modal-no-padding"
      :wrap-class-name="isMobile ? 'ant-modal-bottom-wrap' : ''"
  >
    <template #title>
      <div class="modal-no-padding__header">
        <div class="modal-header">Заявка на продукты</div>
      </div>
      <a-divider style="margin: 16px 0" />
    </template>

    <div class="order-form-modal">
      <div v-if="!showResult" class="order-form-modal__main">
        <div class="modal-no-padding__body order-form-modal__body">
          <a-form
              name="createUser"
              layout="vertical"
              :model="orderForm"
              class="order-form-modal__form"
              @finish="submitOrderForm"
          >
            <div>
              <div class="form-group-row">
                <a-form-item
                    label="Компания"
                    class="input-form-item"
                    name="company"
                >
                  <a-input
                      name="company"
                      v-model:value="orderForm.company"
                      style="min-width: 50%"
                      placeholder="Введите название"
                  />
                </a-form-item>

                <a-form-item
                    label="Ваше ФИО"
                    class="input-form-item"
                    name="name"
                    :rules="[{ required: true, message: 'Введите имя' }]"
                >
                  <a-input
                      name="name"
                      v-model:value="orderForm.name"
                      style="min-width: 50%"
                      placeholder="Введите имя"
                  />
                </a-form-item>
              </div>

              <div class="form-group-row">
                <a-form-item
                    label="Почта"
                    class="input-form-item"
                    name="email"
                    :rules="[
                    { required: true, message: 'Введите почту' },
                    { type: 'email', message: 'Неверный формат' },
                  ]"
                >
                  <a-input
                      name="email"
                      v-model:value="orderForm.email"
                      placeholder="example@mail.ru"
                  />
                </a-form-item>

                <a-form-item
                    label="Номер телефона"
                    class="input-form-item"
                    name="telephone"
                    :rules="[
                    { required: true, message: 'Введите номер телефона' },
                  ]"
                >
                  <a-input
                      v-model:value="orderForm.telephone"
                      name="telephone"
                      type="tel"
                      inputmode="tel"
                      pattern="[0-9\(\)]*"
                      placeholder="7(000)0000000"
                      @beforeinput="preventTelephoneInput"
                      @change="sanitizeTelephoneInput"
                  />
                </a-form-item>
              </div>

              <a-form-item
                  label="Комментарий к заявке"
                  class="input-form-item"
                  name="comment"
                  :rules="[
                    {
                      validator: validateOrderComment,
                      trigger: 'change',
                    },
                  ]"
              >
                <a-textarea
                    v-model:value="orderForm.comment"
                    placeholder="Комментарий"
                    :auto-size="isMobile ? { minRows: 1, maxRows: 5 } : { minRows: 5 }"
                />
              </a-form-item>
            </div>

            <template v-if="quotasLoaded">
              <div v-if="!isMobile" class="order-list">
                <a-table
                    v-for="table in visibleOrderTables"
                    :key="table.key"
                    :columns="getExtendedColumns(table.key, table.title)"
                    :class="getTableClass(table.key)"
                    rowKey="product_id"
                    :data-source="tableData[table.key]"
                    :table-layout="'fixed'"
                    :scroll="getTableScroll(table.key)"
                    :customHeaderRow="getTableHeaderRow(table.key)"
                    :pagination="false"
                    :locale="{ emptyText: emptySlot() }"
                >
                  <template #bodyCell="{ column, text, record }">
                    <template v-if="column.dataIndex === 'free'">
                      <span class="free-quotas">{{ text }}</span>
                    </template>

                    <template v-if="column.dataIndex === 'annual_price'">
                      <span class="annual-price-cell">{{ formatAnnualPriceCell(text) }}</span>
                    </template>

                    <template v-if="column.dataIndex === 'quota'">
                      <a-input-number
                          :value="getProductCount(table.key, record.product_id)"
                          class="quantity-input"
                          :class="{ 'quantity-input--zero': isProductCountZero(table.key, record.product_id) }"
                          :formatter="(value) => formatQuotaNumber(value, true)"
                          :parser="parseQuotaNumber"
                          :min="0"
                          :max="getProductMax(table.key, record.product_id)"
                          :disabled="isProductQuantityDisabled(table.key, record.product_id)"
                          @update:value="(value) => setProductCount(table.key, record.product_id, value)"
                      >
                        <template #addonBefore>
                          <button
                              type="button"
                              class="quantity-addon-button"
                              :class="{
                                'quantity-addon-button--disabled':
                                  isProductAtMin(table.key, record.product_id) ||
                                  isProductQuantityDisabled(table.key, record.product_id),
                              }"
                              :disabled="
                                isProductAtMin(table.key, record.product_id) ||
                                isProductQuantityDisabled(table.key, record.product_id)
                              "
                              @click.stop.prevent="changeProductCount(table.key, record.product_id, -1)"
                          >
                            <MinusOutlined />
                          </button>
                        </template>

                        <template #addonAfter>
                          <button
                              type="button"
                              class="quantity-addon-button"
                              :class="{
                                'quantity-addon-button--disabled':
                                  isProductAtMax(table.key, record.product_id) ||
                                  isProductQuantityDisabled(table.key, record.product_id),
                              }"
                              :disabled="
                                isProductAtMax(table.key, record.product_id) ||
                                isProductQuantityDisabled(table.key, record.product_id)
                              "
                              @click.stop.prevent="changeProductCount(table.key, record.product_id, 1)"
                          >
                            <PlusOutlined />
                          </button>
                        </template>
                      </a-input-number>
                    </template>
                  </template>
                </a-table>
              </div>

              <div v-if="isMobile" class="order-list-mobile">
                <div
                    v-for="table in mobileTables"
                    :key="table.key"
                    v-show="table.rows.length"
                    class="mobile-product-table"
                    :class="{ 'mobile-product-table--collapsed': !expandedMap[table.key] }"
                >
                  <div class="mobile-product-table__header" @click="toggleTable(table.key)">
                    <div class="mobile-product-table__header-toggle">
                      <span
                          class="custom-collapse-expand-icon"
                          :class="{ 'custom-collapse-expand-icon--active': expandedMap[table.key] }"
                          @click.stop="toggleTable(table.key)"
                      >
                        <component :is="expandedMap[table.key] ? MinusOutlined : PlusOutlined" />
                      </span>
                    </div>

                    <div class="mobile-product-table__header-title">
                      {{ table.title }}
                    </div>

                    <div class="mobile-product-table__header-total">
                      {{ formatQuotaNumber(orderForm.entities[table.key].total, true) }}
                    </div>
                  </div>

                  <div
                      v-if="renderedMap[table.key]"
                      class="mobile-product-table__body"
                      :class="{
                      'mobile-product-table__body--open': expandedMap[table.key],
                      'mobile-product-table__body--closing': !expandedMap[table.key],
                    }"
                  >
                    <div
                        v-for="record in table.rows"
                        :key="record.product_id"
                        class="mobile-product-table__row"
                    >
                      <div class="mobile-product-table__product">
                        <div class="mobile-product-table__product-heading">
                          <div class="mobile-product-table__product-name">
                            {{ record.product }}
                          </div>

                          <div class="mobile-product-table__free">
                            Ост: {{ record.free }}
                          </div>
                        </div>

                        <div class="mobile-product-table__product-controls">
                          <div class="mobile-product-table__pricing">
                            <div class="mobile-product-table__price">
                              {{ formatAnnualPriceCell(record.annual_price) }}
                              <span v-if="record.annual_price">&#8381;</span>
                            </div>

                            <div class="mobile-product-table__unit">
                              {{ mobileUnitText(table.key) }}
                            </div>
                          </div>

                          <div class="mobile-product-table__quota">
                            <a-input-number
                                :value="getProductCount(table.key, record.product_id)"
                                class="quantity-input"
                                :class="{ 'quantity-input--zero': isProductCountZero(table.key, record.product_id) }"
                                :formatter="(value) => formatQuotaNumber(value, true)"
                                :parser="parseQuotaNumber"
                                :min="0"
                                :max="getProductMax(table.key, record.product_id)"
                                :disabled="isProductQuantityDisabled(table.key, record.product_id)"
                                @update:value="(value) => setProductCount(table.key, record.product_id, value)"
                            >
                              <template #addonBefore>
                                <button
                                    type="button"
                                    class="quantity-addon-button"
                                    :class="{
                                    'quantity-addon-button--disabled':
                                      isProductAtMin(table.key, record.product_id) ||
                                      isProductQuantityDisabled(table.key, record.product_id),
                                  }"
                                    :disabled="
                                    isProductAtMin(table.key, record.product_id) ||
                                    isProductQuantityDisabled(table.key, record.product_id)
                                  "
                                    @click.stop.prevent="changeProductCount(table.key, record.product_id, -1)"
                                >
                                  <MinusOutlined />
                                </button>
                              </template>

                              <template #addonAfter>
                                <button
                                    type="button"
                                    class="quantity-addon-button"
                                    :class="{
                                    'quantity-addon-button--disabled':
                                      isProductAtMax(table.key, record.product_id) ||
                                      isProductQuantityDisabled(table.key, record.product_id),
                                  }"
                                    :disabled="
                                    isProductAtMax(table.key, record.product_id) ||
                                    isProductQuantityDisabled(table.key, record.product_id)
                                  "
                                    @click.stop.prevent="changeProductCount(table.key, record.product_id, 1)"
                                >
                                  <PlusOutlined />
                                </button>
                              </template>
                            </a-input-number>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="loading-block" v-else>
              <a-spin />
            </div>
          </a-form>
        </div>

        <template v-if="!isMobile">
          <a-divider style="margin: 16px 0" />

          <div class="modal-no-padding__footer order-form-footer">
            <div class="order-footer-summary">
              <div class="order-footer-summary__price">
                {{ formattedTotalOrderPrice }} <span>&#8381;</span>
              </div>
              <div class="order-footer-summary__meta">
                Решения: {{ analyticsProductsToOrder }} / Доступы: {{ otherProductsToOrder }}
              </div>
            </div>

            <div class="button-row">
              <a-col>
                <a-button
                    @click="submitOrderForm"
                    html-type="submit"
                    :disabled="!totalProductsToOrder"
                    class="primary-button"
                    :loading="isProcessing"
                >
                  Отправить заявку
                </a-button>
              </a-col>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="mobile-order-footer">
            <div class="mobile-order-footer__summary">
              <div class="mobile-order-footer__meta">
                Продукты: {{ analyticsProductsToOrder }} / Доступы: {{ otherProductsToOrder }}
              </div>

              <div class="mobile-order-footer__price">
                {{ formattedTotalOrderPrice }} <span>&#8381;</span>
              </div>
            </div>

            <a-divider class="mobile-order-footer__divider" />

            <div class="mobile-order-footer__actions">
              <a-button
                  @click="submitOrderForm"
                  html-type="submit"
                  :disabled="!totalProductsToOrder"
                  class="primary-button mobile-order-footer__button"
                  :loading="isProcessing"
              >
                Отправить заявку
              </a-button>
            </div>
          </div>
        </template>
      </div>

      <div v-if="showResult">
        <div class="form-successful modal-no-padding__body">
          <div class="successful-text">
            <template v-if="successfullySubmitted">
              <SmileOutlined class="happy-smile" />
              <p class="success-line">Заявка успешно создана</p>
              <span class="font-weight-normal">Статус заявки можно отслеживать на странице Заказов.</span>
            </template>
          </div>
        </div>

        <a-divider style="margin: 16px 0" />

        <div class="modal-no-padding__footer">
          <div class="button-row">
            <a-button class="secondary-button" @click="closeForm">Закрыть</a-button>
            <a-button
                v-if="showOrdersButton"
                class="secondary-button"
                @click="() => router.push('/my-orders')"
            >
              Перейти в заказы
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  FrownOutlined,
  MinusOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SmileOutlined,
  UploadOutlined,
} from '@ant-design/icons-vue';
import {onBeforeMount, reactive, ref, watch} from '@vue/runtime-core';
import {useRouter} from 'vue-router';
import {h} from 'vue';
import NotFoundIcon from '@/assets/images/icons/no_data.svg';
import { buildOrderLeadProducts, buildOrderLeadRequest } from '@/utils/orderLead';
import getReportName from '@/mixins/getReportName';
import {computed} from '@vue/reactivity';
import {useStore} from 'vuex';
import {message} from 'ant-design-vue';
import analyticsTranslate from '@/mixins/dictionaries/analyticsTranslate';
import useBreakpoints from '@/mixins/useBreakpoints';
import catalogProducts from '@/views/Catalog/products.json';
import {
  buildCatalogFixedPriceRules,
  buildCatalogPriceRules,
  CATALOG_CONTEXT_SECTIONS,
  createCatalogOrderRow,
  DEFAULT_CATALOG_EMPLOYEE_COUNT,
  findCatalogFixedPriceForOrderRow,
  findCatalogPricesForOrderRow,
  getCatalogSectionProducts,
  getTierPrice,
  createCatalogSubscriptionRow,
  resolveCatalogProductOrderKey,
  resolveCatalogSectionOrderKey,
  resolveCatalogSubscriptionInOrderForm,
  resolveOrderProductId,
  resolveOrderProductMinCount,
  resolveOrderRowMinCount,
} from '@/utils/catalogPricing';

export default {
  name: 'new-order-form',
  components: {
    ExclamationCircleOutlined,
    SmileOutlined,
    FrownOutlined,
    PlusOutlined,
    MinusOutlined,
    UploadOutlined,
    PaperClipOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CloseOutlined,
  },
  emits: ['close', 'submitted'],
  props: {
    open: Boolean,
    showOrdersButton: {
      type: Boolean,
      default: true,
    },
    initialOpenTables: {
      type: Array,
      default: () => [],
    },
    fromCatalog: {
      type: Boolean,
      default: false,
    },
    initialCatalogQuota: {
      type: Number,
      default: null,
    },
    initialCatalogQuotas: {
      type: Object,
      default: null,
    },
    initialCatalogSectionKey: {
      type: String,
      default: null,
    },
    initialCatalogProduct: {
      type: Object,
      default: null,
    },
    initialCatalogSubscription: {
      type: Object,
      default: null,
    },
  },
  setup(props, context) {
    const router = useRouter();
    const store = useStore();
    const successfullySubmitted = ref(false);
    const isProcessing = ref(false);
    const showResult = ref(false);
    const maxProductCount = 50000;

    const priceRules = {
      ...buildCatalogPriceRules(catalogProducts),
    };

    const fixedPriceRules = {
      ...buildCatalogFixedPriceRules(catalogProducts),
    };

    const catalogPricingQuotas = reactive({
      test: {},
      courses: {},
      professionalDevelopment: {},
      expertSupport: {},
      analytics: {},
    });

    const productMinCounts = reactive({
      test: {},
      courses: {},
      professionalDevelopment: {},
      expertSupport: {},
      analytics: {},
      subscriptions: {},
    });

    const orderForm = reactive({
      company: '',
      name: '',
      email: '',
      telephone: '',
      comment: '',
      entities: {
        test: {
          total: 0,
        },
        courses: {
          total: 0,
        },
        professionalDevelopment: {
          total: 0,
        },
        expertSupport: {
          total: 0,
        },
        analytics: {
          total: 0,
        },
        subscriptions: {
          items: [],
          total: 0,
        },
      },
    });

    const allowedTelephoneInput = /^[0-9()]+$/;
    const sanitizeTelephoneValue = (value) => String(value ?? '').replace(/[^0-9()]/g, '');

    const preventTelephoneInput = (event) => {
      if (event.data && !allowedTelephoneInput.test(event.data)) {
        event.preventDefault();
      }
    };

    const sanitizeTelephoneInput = () => {
      orderForm.telephone = sanitizeTelephoneValue(orderForm.telephone);
    };

    const formatQuotaNumber = (value, showZero = false) => {
      if (!value) {
        return showZero ? '0' : '';
      }

      const [integerPart, decimalPart] = String(value).split('.');
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

      return decimalPart === undefined ? formattedInteger : `${formattedInteger}.${decimalPart}`;
    };

    const formatAnnualPriceCell = (value) => formatQuotaNumber(value);

    const parseQuotaNumber = (value) => {
      const parsedValue = String(value ?? '').replace(/\s/g, '').replace(/[^\d.]/g, '');

      return parsedValue === '' ? 0 : Number(parsedValue);
    };

    const getProductUnitPrice = (key, productId) => {
      const product = (tableData[key] ?? []).find((row) => String(row.product_id) === String(productId));

      return Number(product?.annual_price) || 0;
    };

    const validateOrderComment = async (_rule, value) => {
      const trimmedValue = String(value ?? '').trim();

      if (!trimmedValue) {
        return Promise.resolve();
      }

      if (trimmedValue.length < 10) {
        return Promise.reject('Комментарий должен содержать не менее 10 символов');
      }

      if (trimmedValue.length > 5000) {
        return Promise.reject('Комментарий не должен превышать 5000 символов');
      }

      return Promise.resolve();
    };

    const submitOrderForm = () => {
      sanitizeTelephoneInput();

      if (!String(orderForm.telephone ?? '').trim()) {
        message.error('Введите номер телефона');
        return;
      }

      const products = buildOrderLeadProducts(
          orderForm.entities,
          tableData,
          {
            getReportName,
            analyticsTranslate,
            companyQuotas: companyQuotas.value,
            getProductUnitPrice,
            availableSubscriptions: availableSubscriptions.value,
          },
      );

      if (!products.length) {
        message.error('Выберите хотя бы один продукт');
        return;
      }

      isProcessing.value = true;

      const orderObject = buildOrderLeadRequest({
        name: orderForm.name,
        email: orderForm.email,
        phone: orderForm.telephone,
        company: orderForm.company,
        comment: orderForm.comment,
        products,
      });

      store
          .dispatch('relations/createOrderLead', orderObject)
          .then(() => {
            showResult.value = true;
            successfullySubmitted.value = true;
            context.emit('submitted');
          })
          .catch(() => {
            message.error('Произошла ошибка');
          })
          .finally(() => {
            isProcessing.value = false;
          });
    };

    const closeForm = () => {
      store.dispatch('relations/clearInsufficientProducts');
      context.emit('close');
    };

    const orderTableKeys = [
      'test',
      'courses',
      'professionalDevelopment',
      'expertSupport',
      'analytics',
      'subscriptions',
    ];
    const mobileOrderTableKeys = [
      'subscriptions',
      'test',
      'courses',
      'professionalDevelopment',
      'expertSupport',
      'analytics',
    ];
    const quantityTableKeys = [
      'test',
      'courses',
      'professionalDevelopment',
      'expertSupport',
      'analytics',
    ];

    const orderTableAliases = {
      passings: 'test',
    };

    const specialHeaders = {
      test: 'за 1 шт.',
      courses: 'за 1 шт.',
      professionalDevelopment: 'за 1 шт.',
      expertSupport: 'за 1 шт.',
      analytics: 'за 1 шт.',
      subscriptions: 'За чел 1 / год',
    };

    const mobileTableTitles = {
      test: 'Оценка',
      courses: 'Развитие',
      professionalDevelopment: 'Профессиональное развитие',
      expertSupport: 'Экспертное сопровождение',
      analytics: 'Аналитика',
      subscriptions: 'Подписка',
    };

    const orderTableTitles = {
      test: 'Оценка ЭИ',
      courses: 'Развитие',
      professionalDevelopment: 'Профессиональное развитие',
      expertSupport: 'Экспертное сопровождение',
      analytics: 'Аналитика',
      subscriptions: 'Подписки',
    };

    const expandedMap = ref({
      test: true,
      courses: true,
      professionalDevelopment: true,
      expertSupport: true,
      analytics: true,
      subscriptions: true,
    });

    const renderedMap = ref({
      test: true,
      courses: true,
      professionalDevelopment: true,
      expertSupport: true,
      analytics: true,
      subscriptions: true,
    });

    const isCatalogOnlyOrderTable = (key) => key === 'professionalDevelopment' || key === 'expertSupport';

    const hasCatalogOnlyTableRows = (key) => (
        props.fromCatalog && (tableData[key] ?? []).length > 0
    );

    const isOrderTableVisible = (key) => {
      if (key === 'subscriptions') {
        return tableData.subscriptions.length > 0;
      }

      if (isCatalogOnlyOrderTable(key)) {
        return hasCatalogOnlyTableRows(key);
      }

      return true;
    };

    const visibleOrderTables = computed(() => orderTableKeys
        .filter((key) => isOrderTableVisible(key))
        .map((key) => ({
          key,
          title: orderTableTitles[key],
        })));

    const resolveInitialOpenTables = () => (
      props.initialOpenTables.length
          ? props.initialOpenTables
          : store.state.relations.orderFormInitialOpenTables
    );

    const applyInitialOpenTables = () => {
      const initialOpenTables = resolveInitialOpenTables();

      if (initialOpenTables.length) {
        orderTableKeys.forEach((key) => {
          const isOpen = initialOpenTables.some((tableKey) => (orderTableAliases[tableKey] ?? tableKey) === key);

          expandedMap.value[key] = isOpen;
          renderedMap.value[key] = isOpen;
        });
      }
    };

    const toggleTable = (key) => {
      if (expandedMap.value[key]) {
        expandedMap.value[key] = false;

        setTimeout(() => {
          if (!expandedMap.value[key]) {
            renderedMap.value[key] = false;
          }
        }, 120);
      } else {
        renderedMap.value[key] = true;

        requestAnimationFrame(() => {
          expandedMap.value[key] = true;
        });
      }
    };

    const getTableHeaderRow = (tableKey) => () => ({
      class: 'table-collapse-header-row',
      onClick: () => toggleTable(tableKey),
    });

    const getTableScroll = (key) => (expandedMap.value[key] ? { x: 560 } : undefined);

    const getTableClass = (key) => ({
      'order-product-table': true,
      'order-product-table--collapsed': !expandedMap.value[key],
      'order-product-table--open': renderedMap.value[key] && expandedMap.value[key],
      'order-product-table--closing': renderedMap.value[key] && !expandedMap.value[key],
      'order-product-table--hidden': !renderedMap.value[key],
    });

    const getExtendedColumns = (key, mainTitle) => {
      return [
        {
          title: () => renderCollapseElement(key),
          dataIndex: 'toggle',
          key: 'toggle',
          width: 24,
          align: 'center',
          className: 'collapse-toggle-cell',
          customHeaderCell: () => ({ class: 'collapse-toggle-cell' }),
          customCell: () => ({ class: 'collapse-toggle-cell' }),
        },
        {
          title: mainTitle,
          dataIndex: 'product',
          key: 'product',
          width: 200,
          align: 'left',
          className: 'product-name-cell',
          customHeaderCell: () => ({ class: 'product-name-cell' }),
        },
        {
          title: 'Остаток',
          dataIndex: 'free',
          key: 'free',
          width: 80,
          align: 'right',
        },
        {
          title: specialHeaders[key],
          dataIndex: 'annual_price',
          key: 'annual_price',
          width: 110,
          align: 'right',
        },
        {
          title: renderSpecialHeader(key),
          dataIndex: 'quota',
          key: 'quota',
          width: 130,
          align: 'right',
          customCell: () => ({ class: 'quota-input-cell' }),
        },
      ];
    };

    const emptySlot = () =>
        h('div', { class: 'no-data-wrapper' }, [
          h('img', { src: NotFoundIcon, class: 'no-data-image' }),
          h('div', { class: 'no-data-text' }, 'Нет данных для отображения'),
        ]);

    const renderCollapseElement = (key) =>
        h(
            'span',
            {
              class: [
                'custom-collapse-expand-icon',
                expandedMap.value[key] ? 'custom-collapse-expand-icon--active' : '',
              ],
              onClick: (e) => {
                e.stopPropagation();
                toggleTable(key);
              },
            },
            [h(expandedMap.value[key] ? MinusOutlined : PlusOutlined)],
        );

    const renderSpecialHeader = (key) =>
        h(
            'span',
            {
              class: ['selected-quotas'],
            },
            formatQuotaNumber(orderForm.entities[key].total, true),
        );

    const tableData = reactive({
      test: [],
      courses: [],
      professionalDevelopment: [],
      expertSupport: [],
      analytics: [],
      subscriptions: [],
    });

    const companyQuotas = computed(() => store.state.company.userQuotas);
    const insufficientQuotas = computed(() => store.state.relations.insufficientProducts);
    const profile = computed(() => store.state.profile.profile);
    const totalProductsToOrder = ref(0);
    const analyticsProductsToOrder = computed(() => orderForm.entities.analytics.total);
    const otherProductsToOrder = computed(() => totalProductsToOrder.value - analyticsProductsToOrder.value);

    const totalOrderPrice = computed(() =>
        [
          ...tableData.test,
          ...tableData.courses,
          ...tableData.professionalDevelopment,
          ...tableData.expertSupport,
          ...tableData.analytics,
          ...tableData.subscriptions,
        ].reduce((sum, row) => sum + (Number(row.total_price) || 0), 0),
    );

    const formattedTotalOrderPrice = computed(() => formatQuotaNumber(totalOrderPrice.value) || '0');

    const quotasLoaded = ref(true);
    const availableSubscriptions = computed(() => (
        (catalogProducts.subscription ?? [])
            .map((subscription) => ({
              id: subscription.title,
              title: subscription.title,
              price: subscription.price,
            }))
    ));

    const mobileTables = computed(() =>
        mobileOrderTableKeys
            .filter((key) => isOrderTableVisible(key))
            .map((key) => ({
              key,
              title: mobileTableTitles[key],
              rows: tableData[key] ?? [],
            })),
    );

    const mobileUnitText = (key) => {
      if (key === 'subscriptions') {
        return 'За человека в год';
      }

      return 'За 1 шт.';
    };

    onBeforeMount(async () => {
      quotasLoaded.value = false;

      await store.dispatch('company/getUserQuotas').then(() => {
        prepareData();
        quotasLoaded.value = true;
      });
    });

    const enrichRowWithCatalogPrices = (tableKey, row) => {
      const catalogPrices = findCatalogPricesForOrderRow(tableKey, row, catalogProducts)
          ?? priceRules[row.product_id];

      if (catalogPrices) {
        priceRules[row.product_id] = catalogPrices;
      }

      const catalogFixedPrice = findCatalogFixedPriceForOrderRow(tableKey, row, catalogProducts)
          ?? fixedPriceRules[row.product_id];

      if (catalogFixedPrice != null) {
        fixedPriceRules[row.product_id] = catalogFixedPrice;
        row.original_price = catalogFixedPrice;
      }
    };

    const addCatalogSectionProductsToTable = (sectionKey) => {
      const orderKey = resolveCatalogSectionOrderKey(catalogProducts, sectionKey);

      if (!orderKey) {
        return;
      }

      const tableKey = orderTableAliases[orderKey] ?? orderKey;

      getCatalogSectionProducts(sectionKey, catalogProducts).forEach((product, index) => {
        const productId = resolveCatalogProductOrderKey(product, sectionKey, index);
        const exists = (tableData[tableKey] ?? []).some(
            (row) => String(row.product_id) === String(productId),
        );

        if (exists) {
          return;
        }

        tableData[tableKey].push(createCatalogOrderRow(product, productId));

        if (orderForm.entities[tableKey][productId] == null) {
          orderForm.entities[tableKey][productId] = 0;
        }
      });
    };

    const ensureCatalogProductsInOrderTables = () => {
      if (!props.fromCatalog) {
        return;
      }

      CATALOG_CONTEXT_SECTIONS.forEach((sectionKey) => {
        addCatalogSectionProductsToTable(sectionKey);
      });
    };

    const applyCatalogSectionPricingQuotas = () => {
      if (!props.fromCatalog || !props.initialCatalogQuotas) {
        return;
      }

      CATALOG_CONTEXT_SECTIONS.forEach((sectionKey) => {
        const orderKey = resolveCatalogSectionOrderKey(catalogProducts, sectionKey);
        const sectionQuota = props.initialCatalogQuotas[sectionKey];

        if (!orderKey || sectionQuota == null) {
          return;
        }

        const tableKey = orderTableAliases[orderKey] ?? orderKey;

        getCatalogSectionProducts(sectionKey, catalogProducts).forEach((product, index) => {
          const productId = resolveCatalogProductOrderKey(product, sectionKey, index);

          if (priceRules[productId] || product.prices) {
            catalogPricingQuotas[tableKey][productId] = Number(sectionQuota);
          }
        });
      });
    };

    const getOpenedTableKeys = () => {
      const initialOpenTables = resolveInitialOpenTables();

      if (!initialOpenTables.length) {
        return orderTableKeys;
      }

      return initialOpenTables.map((tableKey) => orderTableAliases[tableKey] ?? tableKey);
    };

    const applyDefaultProductMins = () => {
      quantityTableKeys.forEach((tableKey) => {
        (tableData[tableKey] ?? []).forEach((row) => {
          const minCount = resolveOrderRowMinCount(tableKey, row, catalogProducts);

          if (minCount > 0) {
            const currentMin = getProductMin(tableKey, row.product_id);
            setProductMinCount(tableKey, row.product_id, Math.max(currentMin, minCount));
          }
        });
      });
    };

    const enforceProductMins = () => {
      quantityTableKeys.forEach((tableKey) => {
        (tableData[tableKey] ?? []).forEach((row) => {
          const minCount = getProductMin(tableKey, row.product_id);
          const count = getProductCount(tableKey, row.product_id);

          if (count > 0 && minCount > 0 && count < minCount) {
            setProductCount(tableKey, row.product_id, minCount);
          }
        });
      });
    };

    const applyOrderFormProductRules = () => {
      if (props.fromCatalog) {
        ensureCatalogProductsInOrderTables();

        quantityTableKeys.forEach((tableKey) => {
          (tableData[tableKey] ?? []).forEach((row) => enrichRowWithCatalogPrices(tableKey, row));
        });
      }

      applyDefaultProductMins();
      applyCatalogSectionPricingQuotas();
      applyCatalogInitialContext();
      applyCatalogSubscriptionContext();
      enforceProductMins();
      orderTableKeys.forEach(recalculateProductPrices);
      quantityTableKeys.forEach(recalculateTotals);
      recalculateTotals('subscriptions');
    };

    const getCatalogTargetTableKeys = () => {
      if (props.initialCatalogSectionKey) {
        const section = (catalogProducts.sections ?? []).find(
            (item) => item.key === props.initialCatalogSectionKey,
        );
        const orderKey = section?.orderKey;

        if (orderKey) {
          const tableKey = orderTableAliases[orderKey] ?? orderKey;

          return tableKey ? [tableKey] : [];
        }
      }

      return getOpenedTableKeys();
    };

    const applyCatalogInitialContext = () => {
      if (!props.initialCatalogProduct) {
        return;
      }

      const targetTableKeys = getCatalogTargetTableKeys();
      const catalogProduct = {
        ...props.initialCatalogProduct,
        sectionKey: props.initialCatalogSectionKey,
      };
      const orderMinCount = resolveOrderProductMinCount(catalogProduct);
      const initialCount = orderMinCount > 0
          ? orderMinCount
          : DEFAULT_CATALOG_EMPLOYEE_COUNT;

      targetTableKeys.forEach((tableKey) => {
        const rows = tableData[tableKey] ?? [];

        if (!rows.length) {
          return;
        }

        const resolvedProductId = resolveOrderProductId(
            catalogProducts,
            props.initialCatalogProduct,
            tableKey,
            rows,
        );

        if (resolvedProductId == null) {
          return;
        }

        if (orderMinCount > 0) {
          setProductMinCount(tableKey, resolvedProductId, orderMinCount);
        }

        if (props.initialCatalogQuota != null) {
          catalogPricingQuotas[tableKey][resolvedProductId] = Number(props.initialCatalogQuota);
        }

        setProductCount(tableKey, resolvedProductId, initialCount);
      });
    };

    const applyCatalogSubscriptionContext = () => {
      if (!props.fromCatalog || !props.initialCatalogSubscription) {
        return;
      }

      const { productId, row } = resolveCatalogSubscriptionInOrderForm(
          tableData.subscriptions,
          props.initialCatalogSubscription,
      );

      if (!row) {
        tableData.subscriptions.push(
            createCatalogSubscriptionRow(props.initialCatalogSubscription, productId),
        );

        if (orderForm.entities.subscriptions[productId] == null) {
          orderForm.entities.subscriptions[productId] = 0;
        }
      }

      expandedMap.value.subscriptions = true;
      renderedMap.value.subscriptions = true;
      setProductCount('subscriptions', productId, 1);
    };

    const resetCatalogProductContext = () => {
      orderTableKeys.forEach((key) => {
        productMinCounts[key] = {};
      });

      quantityTableKeys.forEach((key) => {
        catalogPricingQuotas[key] = {};
      });
    };

    const setProductMinCount = (key, productId, minCount) => {
      productMinCounts[key][productId] = Number(minCount) || 0;
    };

    const getProductMin = (key, productId) => {
      const minCount = productMinCounts[key]?.[productId];

      return minCount == null ? 0 : Number(minCount);
    };

    const shouldUseCatalogContext = () => Boolean(props.initialCatalogProduct);

    const isCatalogTargetProduct = (tableKey, productId) => {
      if (!shouldUseCatalogContext()) {
        return false;
      }

      const rows = tableData[tableKey] ?? [];
      const resolvedProductId = resolveOrderProductId(
          catalogProducts,
          props.initialCatalogProduct,
          tableKey,
          rows,
      );

      return resolvedProductId != null && String(resolvedProductId) === String(productId);
    };

    const resolveInitialProductCount = (type, productId) => {
      if (isCatalogTargetProduct(type, productId)) {
        return null;
      }

      return calculateQuotasToAppend(type, productId);
    };

    const prepareData = () => {
      resetCatalogProductContext();
      tableData.test = Object.entries(companyQuotas.value.test_passings)
          .filter(([key]) => key !== 'empty')
          .map(([key, value]) => {
            const quotasToAppend = resolveInitialProductCount('test', key);

            orderForm.entities.test[key] = quotasToAppend === null ? 0 : (Number(quotasToAppend) || 0);

            return {
              product: getReportName(key),
              product_id: key,
              free: value.free,
              quota: orderForm.entities.test[key],
              annual_price: '',
              original_price: '',
              total_price: 0,
            };
          });

      tableData.courses = Object.entries(companyQuotas.value.course_passings.total).map(([key, value]) => {
        let freePassings = companyQuotas.value.course_passings.free.find((course) => course.course_id === value.course_id);

        const quotasToAppend = resolveInitialProductCount('courses', value.course_id);

        orderForm.entities.courses[value.course_id] = quotasToAppend === null
            ? 0
            : (Number(quotasToAppend) || 0);

        return {
          product: value.name,
          product_id: value.course_id,
          free: freePassings?.count ?? 0,
          quota: orderForm.entities.courses[value.course_id],
          annual_price: '',
          original_price: '',
          total_price: 0,
        };
      });

      const analyticsQuotasToAppend = calculateQuotasToAppend('analytics', 'reference_profile');
      orderForm.entities.analytics.reference_profile = analyticsQuotasToAppend || 0;

      tableData.analytics = [
        {
          product: 'Эталонный профиль',
          product_id: 'reference_profile',
          free: companyQuotas.value?.reference_profiles?.count
              ?? companyQuotas.value?.reference_profiles?.free_count
              ?? 0,
          quota: analyticsQuotasToAppend,
          annual_price: '',
          original_price: '',
          total_price: 0,
          validUntil: '',
          timeRemaining: null,
        },
      ];

      checkAnalyticsQuotaToAppend();

      tableData.professionalDevelopment = [];
      tableData.expertSupport = [];
      tableData.subscriptions = getLatestSubscription();

      quantityTableKeys.forEach((tableKey) => {
        (tableData[tableKey] ?? []).forEach((row) => enrichRowWithCatalogPrices(tableKey, row));
      });

      applyOrderFormProductRules();
    };

    const getLatestSubscription = () => {
      return availableSubscriptions.value.map((subscription) => {
        orderForm.entities.subscriptions[subscription.id] = 0;

        return createCatalogSubscriptionRow(subscription, subscription.id);
      });
    };

    const calculateQuotasToAppend = (type, product) => {
      let quotasToOrder = 0;

      if (insufficientQuotas.value) {
        let group = insufficientQuotas.value[type];

        if (group) {
          let insufficientProduct = group.find((el) => el.product_id === product);
          quotasToOrder = insufficientProduct ? insufficientProduct.count : 0;
        }
      }

      return quotasToOrder;
    };

    const checkAnalyticsQuotaToAppend = () => {
      tableData.analytics.forEach((el) => {
        let group = insufficientQuotas.value.analytics ?? [];
        let insufficientProduct = group.find((inProduct) => inProduct.product_id === el.product_id);

        if (insufficientProduct) {
          orderForm.entities.analytics[el.product_id] = insufficientProduct.count;
        } else {
          orderForm.entities.analytics[el.product_id] = 0;
        }
      });
    };

    const getProductCount = (key, productId) => Number(orderForm.entities[key]?.[productId]) || 0;

    const getProductMax = (key, productId) => {
      return maxProductCount;
    };

    const isProductCountZero = (key, productId) => getProductCount(key, productId) <= 0;
    const isProductAtMin = (key, productId) => getProductCount(key, productId) <= 0;
    const isProductAtMax = (key, productId) => getProductCount(key, productId) >= getProductMax(key, productId);

    const isProductQuantityDisabled = (key, productId) => {
      return key === 'subscriptions' && getProductMax(key, productId) === 0 && isProductAtMin(key, productId);
    };

    const setProductCount = (key, productId, value) => {
      const minCount = getProductMin(key, productId);
      const parsedValue = value === '' || value == null ? 0 : Number(value);

      if (Number.isNaN(parsedValue)) {
        return;
      }

      if (parsedValue === 0) {
        orderForm.entities[key][productId] = 0;
      } else if (minCount > 0 && parsedValue < minCount) {
        orderForm.entities[key][productId] = 0;
      } else {
        orderForm.entities[key][productId] = Math.min(
            parsedValue,
            getProductMax(key, productId),
        );
      }

      updateProductPrice(key, productId);
      recalculateTotals(key);
    };

    const changeProductCount = (key, productId, step) => {
      const current = getProductCount(key, productId);
      const minCount = getProductMin(key, productId);

      if (step > 0 && current <= 0 && minCount > 0) {
        setProductCount(key, productId, minCount);
        return;
      }

      setProductCount(key, productId, current + step);
    };

    const getProductPrice = (row, key, count) => {
      const fixedPrice = fixedPriceRules[row.product_id];

      if (fixedPrice != null) {
        return fixedPrice;
      }

      const catalogPrices = priceRules[row.product_id];

      if (catalogPrices) {
        const tierQuantity = catalogPricingQuotas[key]?.[row.product_id] ?? count;
        const price = getTierPrice(catalogPrices, tierQuantity);

        return price ?? row.original_price ?? '';
      }

      return row.original_price ?? '';
    };

    const updateProductPrice = (key, productId) => {
      const row = (tableData[key] ?? []).find((product) => product.product_id === productId);
      const orderCount = getProductCount(key, productId);

      row.annual_price = getProductPrice(row, key, orderCount);
      row.total_price = orderCount * (Number(row.annual_price) || 0);
    };

    const recalculateProductPrices = (key) => {
      (tableData[key] ?? []).forEach((row) => {
        updateProductPrice(key, row.product_id);
      });
    };

    const recalculateTotals = (key) => {
      let entity = orderForm.entities[key];

      if (key === 'subscriptions') {
        const subscriptionItems = Object.entries(entity)
            .filter(([entryKey, value]) => entryKey !== 'total' && entryKey !== 'items' && Number(value) > 0)
            .map(([entryKey]) => entryKey);

        orderForm.entities.subscriptions.items = subscriptionItems;
        orderForm.entities.subscriptions.total = subscriptionItems
            .reduce((sum, subscriptionId) => sum + (Number(entity[subscriptionId]) || 0), 0);
      } else if (key === 'analytics') {
        orderForm.entities[key].total = Object.entries(entity)
            .filter(([entryKey, v]) => entryKey !== 'total' && v)
            .reduce((sum, [, value]) => sum + (Number(value) || 0), 0);
      } else {
        orderForm.entities[key].total = Object.entries(entity)
            .filter(([entryKey]) => entryKey !== 'total' && entryKey !== 'items')
            .reduce((sum, [, v]) => sum + (Number(v) || 0), 0);
      }

      calculateTotalProducts();
    };

    const calculateTotalProducts = () => {
      totalProductsToOrder.value = orderForm.entities.courses.total +
          orderForm.entities.professionalDevelopment.total +
          orderForm.entities.expertSupport.total +
          orderForm.entities.test.total +
          orderForm.entities.analytics.total +
          orderForm.entities.subscriptions.total;
    };

    watch(() => profile.value, () => {
        orderForm.name = (profile.value?.first_name + ' ' + (profile.value?.last_name ?? '')) ?? '';
        orderForm.email = profile.value?.email ?? '';
        orderForm.company = profile.value?.company ? profile.value?.company.name : '';
    }, { immediate: true });

    watch(() => props.open, (isOpen) => {
      if (isOpen) {
        applyInitialOpenTables();
      }
    }, { immediate: true });

    watch(
        () => [
          props.open,
          props.fromCatalog,
          props.initialCatalogQuota,
          props.initialCatalogQuotas,
          props.initialCatalogSectionKey,
          props.initialCatalogProduct,
          props.initialCatalogSubscription,
          quotasLoaded.value,
        ],
        ([isOpen, , , , , , , loaded]) => {
          if (isOpen && loaded) {
            applyOrderFormProductRules();
          }
        },
    );

    const { isMobile } = useBreakpoints();

    return {
      router,
      getTableHeaderRow,
      getTableScroll,
      getTableClass,
      getExtendedColumns,
      emptySlot,
      showResult,
      orderForm,
      submitOrderForm,
      validateOrderComment,
      closeForm,
      isProcessing,
      successfullySubmitted,
      tableData,
      quotasLoaded,
      recalculateTotals,
      totalProductsToOrder,
      analyticsProductsToOrder,
      otherProductsToOrder,
      formattedTotalOrderPrice,
      isMobile,
      preventTelephoneInput,
      sanitizeTelephoneInput,
      formatQuotaNumber,
      formatAnnualPriceCell,
      parseQuotaNumber,
      getProductCount,
      getProductMin,
      getProductMax,
      isProductCountZero,
      isProductAtMin,
      isProductAtMax,
      isProductQuantityDisabled,
      setProductCount,
      changeProductCount,
      visibleOrderTables,
      expandedMap,
      renderedMap,
      toggleTable,
      mobileTables,
      mobileUnitText,
      MinusOutlined,
      PlusOutlined,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-group-row {
  display: flex;
  justify-content: space-between;
  column-gap: 10px;
  row-gap: 10px;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;
    row-gap: 0;
  }
}

.input-form-item {
  min-width: 49%;
}

.password-radio-group,
.checkbox-group {
  display: flex;
  flex-direction: column;
  row-gap: 5px;
}

.password-field {
  margin: 10px;
}

:deep(.ant-input-focused) {
  border-color: var(--main-green);
}

.ant-input:hover,
.ant-input:focus {
  border-color: var(--main-green);
}

.ant-input:focus,
.ant-select-focused .ant-select-selector {
  box-shadow: 0 0 0 2px var(--polar-green);
}

.ant-input-affix-wrapper:hover,
.ant-input-affix-wrapper-focused {
  border-color: var(--main-green);
}

.ant-input-affix-wrapper-focused {
  box-shadow: 0 0 0 2px var(--polar-green);
}

:deep(.ant-form-item-label) {
  padding: 0;
}

:deep(.ant-form-item) {
  margin-bottom: 12px;
}

.button-row {
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
}

.order-form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;
    align-items: stretch;
  }
}

.order-footer-summary {
  min-width: 160px;
}

.order-footer-summary__price {
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.order-footer-summary__meta {
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.successful-text {
  margin: 0;
  padding: 0 20px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
}

.successful-text p {
  margin-bottom: 0;
}

.successful-text span {
  font-size: 14px;
  color: var(--grey-text);
}

.successful-text .happy-smile,
.successful-text .frown-smile {
  margin-bottom: 12px;
  font-size: 65px;
}

.successful-text .happy-smile {
  color: var(--main-green);
}

.success-line {
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.successful-text span {
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

@media (max-width: $mobile-breakpoint) {
  .secondary-button {
    width: 100%;
  }
}

.free-quotas {
  color: var(--grey-text);
}

@media (max-width: $mobile-breakpoint) {
  :global(.ant-modal-bottom-wrap .modal-no-padding .ant-modal-content) {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 24px);
  }

  :global(.ant-modal-bottom-wrap .modal-no-padding .ant-modal-body) {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    max-height: none;
    overflow: hidden !important;
  }

  .order-form-modal,
  .order-form-modal__main,
  .order-form-modal__body,
  .order-form-modal__form {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }

  .order-form-modal__body {
    max-height: none !important;
    overflow: hidden !important;
  }
}

.order-list {
  --small-table-border-color: #f0f0f0;

  display: flex;
  flex-direction: column;
  row-gap: 10px;

  :deep(.ant-table-container) {
    overflow: hidden;
    border: 1px solid var(--small-table-border-color);
    border-radius: 8px;
  }

  :deep(.ant-table) {
    border-radius: 8px;
    font-size: 12px;
  }

  :deep(.ant-table-cell) {
    padding: 8px 12px !important;
    font-size: 12px;
    line-height: 16px;
  }

  :deep(.collapse-toggle-cell) {
    min-width: 24px;
    padding-right: 4px !important;
    padding-left: 4px !important;
    overflow: visible !important;
  }

  :deep(.ant-table-thead > tr > th) {
    border-bottom: none !important;
    color: rgba(0, 0, 0, 0.65);
    font-weight: 400;
  }

  :deep(.ant-table-thead > tr > th.product-name-cell),
  :deep(.ant-table-thead .selected-quotas) {
    color: var(--main-font);
    font-weight: 500;
  }

  :deep(.ant-table-tbody > tr > td) {
    border-bottom: none;
  }

  :deep(.ant-table-wrapper .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr:last-child > td) {
    border-bottom: none !important;
  }

  :deep(.ant-table-tbody > .ant-table-row > td),
  :deep(.ant-table-tbody > .ant-table-placeholder > td) {
    border-top: 1px solid var(--small-table-border-color);
  }

  :deep(.ant-table-placeholder > .ant-table-cell) {
    text-align: center !important;
  }

  :deep(.no-data-wrapper) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 24px;
    text-align: center;
  }

  :deep(.ant-table-tbody > .ant-table-measure-row > td) {
    border: 0 !important;
  }

  :deep(.quota-input-cell) {
    padding: 5px 10px !important;
  }

  :deep(.ant-input-number),
  :deep(.ant-input-number-input),
  :deep(.ant-btn) {
    font-size: 12px;
  }

  :deep(.quantity-input.ant-input-number),
  :deep(.quantity-input .ant-input-number) {
    display: inline-flex;
    align-items: center;
    height: 24px;
    color: rgba(0, 0, 0, 0.65);
  }

  :deep(.ant-input-number-group-wrapper.quantity-input) {
    max-width: 114px;
  }

  :deep(.ant-input-number-input-wrap) {
    display: flex;
    align-items: center;
    height: 100%;
    line-height: 22px;
  }

  :deep(.ant-input-number-input) {
    display: block;
    height: 22px;
    padding-top: 0;
    padding-bottom: 0;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.65);
    text-align: center;
  }

  :deep(.quantity-input--zero .ant-input-number-input),
  :deep(.quantity-input--zero .ant-input-number-input::placeholder) {
    color: rgba(0, 0, 0, 0.25);
  }

  :deep(.ant-input-number-group-addon) {
    padding: 0;
    background: #fff;
  }

  :deep(.quantity-addon-button) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(0, 0, 0, 0.65);
    font-size: 10px;
    line-height: 1;
    cursor: pointer;
  }

  :deep(.quantity-addon-button--disabled),
  :deep(.quantity-addon-button:disabled) {
    color: rgba(0, 0, 0, 0.25);
    cursor: default;
  }

  :deep(.annual-price-cell) {
    color: rgba(0, 0, 0, 0.65);
  }
}

.order-list-mobile {
  margin: 4px 0 16px;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  row-gap: 16px;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.mobile-product-table {
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
}

.mobile-product-table:first-child {
  border-top: 1px solid #f0f0f0;
}

.mobile-product-table__header {
  display: grid;
  grid-template-columns: 25px minmax(0, 1fr) max-content;
  align-items: center;
  column-gap: 8px;
  padding: 8px 8px;
  background: #fafafa;
  cursor: pointer;
}

.mobile-product-table__header-toggle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
}

.mobile-product-table__header-toggle::after {
  position: absolute;
  top: 50%;
  right: 0;
  width: 1px;
  height: 16px;
  background: #f0f0f0;
  transform: translateY(-50%);
  content: '';
}

.mobile-product-table__header-toggle :deep(.custom-collapse-expand-icon) {
  margin: 0;
}

.mobile-product-table__header-title {
  min-width: 0;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
}

.mobile-product-table__header-total {
  color: var(--main-font);
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
}

.mobile-product-table__body {
  overflow: hidden;
}

.mobile-product-table__body--open {
  max-height: 2000px;
  opacity: 1;
  transition: max-height 0.12s ease, opacity 0.12s ease;
}

.mobile-product-table__body--closing {
  max-height: 0;
  opacity: 0;
  transition: max-height 0.12s ease, opacity 0.12s ease;
}

.mobile-product-table__row {
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.mobile-product-table__row:last-child {
  border-bottom: 0;
}

.mobile-product-table__product {
  min-width: 0;
}

.mobile-product-table__product-heading,
.mobile-product-table__product-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  column-gap: 16px;
  min-width: 0;
}

.mobile-product-table__product-heading {
  align-items: start;
}

.mobile-product-table__product-controls {
  grid-template-columns: minmax(0, 1fr) minmax(0, 114px);
  align-items: end;
  margin-top: 10px;
}

.mobile-product-table__product-name {
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.mobile-product-table__pricing {
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
}

.mobile-product-table__price {
  color: var(--main-green);
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.mobile-product-table__unit {
  margin-top: 2px;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.mobile-product-table__free {
  flex: 0 0 auto;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.mobile-product-table__quota {
  display: flex;
  min-width: 0;
  justify-content: flex-end;
}

.order-list-mobile {
  :deep(.ant-input-number),
  :deep(.ant-input-number-input),
  :deep(.ant-btn) {
    font-size: 12px;
  }

  :deep(.quantity-input.ant-input-number),
  :deep(.quantity-input .ant-input-number) {
    display: inline-flex;
    align-items: center;
    height: 24px;
    color: rgba(0, 0, 0, 0.65);
  }

  :deep(.ant-input-number-group-wrapper.quantity-input) {
    max-width: 114px;
  }

  :deep(.ant-input-number-input-wrap) {
    display: flex;
    align-items: center;
    height: 100%;
  }

  :deep(.ant-input-number-input) {
    height: 100%;
    padding-top: 0;
    padding-bottom: 0;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.65);
    text-align: center;
  }

  :deep(.quantity-input--zero .ant-input-number-input),
  :deep(.quantity-input--zero .ant-input-number-input::placeholder) {
    color: rgba(0, 0, 0, 0.25);
  }

  :deep(.ant-input-number-group-addon) {
    padding: 0;
    background: #fff;
  }

  :deep(.quantity-addon-button) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(0, 0, 0, 0.65);
    font-size: 10px;
    line-height: 1;
    cursor: pointer;
  }

  :deep(.quantity-addon-button--disabled),
  :deep(.quantity-addon-button:disabled) {
    color: rgba(0, 0, 0, 0.25);
    cursor: default;
  }
}

.mobile-order-footer {
  flex: 0 0 auto;
  background: #fff;
  border-top: 1px solid #f0f0f0;
}

.mobile-order-footer__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
}

.mobile-order-footer__meta {
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.mobile-order-footer__price {
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.mobile-order-footer__divider {
  margin: 0;
}

.mobile-order-footer__actions {
  padding: 16px 20px;
}

.mobile-order-footer__button {
  width: 100%;
}

:deep(.order-product-table--collapsed .ant-table-container),
:deep(.order-product-table--collapsed .ant-table-content),
:deep(.order-product-table--collapsed .ant-table-body) {
  overflow-x: hidden !important;
  scrollbar-width: none;
}

:deep(.order-product-table--collapsed .ant-table-container::-webkit-scrollbar),
:deep(.order-product-table--collapsed .ant-table-content::-webkit-scrollbar),
:deep(.order-product-table--collapsed .ant-table-body::-webkit-scrollbar) {
  display: none;
  width: 0;
  height: 0;
}

:deep(.order-product-table .ant-table-tbody) {
  transform-origin: top;
}

:deep(.order-product-table--open .ant-table-tbody) {
  display: table-row-group;
  animation: tableBodyOpen 0.2s ease;
}

:deep(.order-product-table--closing .ant-table-tbody) {
  display: table-row-group;
  animation: tableBodyClose 0.12s ease forwards;
}

:deep(.order-product-table--hidden .ant-table-tbody) {
  display: none;
}

:deep(.ant-input-number-input),
:deep(.ant-input-number-input::placeholder) {
  text-align: center;
}

:deep(.ant-input-number-handler-wrap) {
  display: none;
}

:deep(.custom-collapse-expand-icon) {
  margin: auto;
  display: flex;
  flex: 0 0 16px;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  background: #fff;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;
  color: var(--main-font);
}

:deep(.custom-collapse-expand-icon--active) {
  transform: translateY(1px);
}

:deep(.table-collapse-header-row),
:deep(.table-collapse-header-row > th) {
  cursor: pointer;
}
</style>
